import { ApiError, type FieldProblem } from '../net/errors.js'

/**
 * Fehler an das Feld hängen, zu dem sie gehören.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was vorher passierte
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Der Server liefert seit jeher eine LISTE – `{ feld, problem }` je Punkt, und
 * `pruefeAbschluss` füllt sie sorgfältig. Der Client transportierte sie auch
 * (`ApiError.options.fields`) und warf dann das `feld` weg: Alles wurde zu
 * einem Satz zusammengeklebt und als Toast angezeigt. Wer «Die Eingaben sind
 * unvollständig» las, wusste nicht, wo.
 *
 * Diese Datei ist die fehlende Hälfte: Sie ordnet jedem Serverfeld eine
 * Eingabe auf dem Bildschirm zu, damit der rote Rahmen und der Satz darunter
 * dort erscheinen, wo etwas fehlt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum eine Zuordnungstabelle und keine Namensgleichheit
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil die beiden Seiten verschiedene Dinge benennen. Der Server kennt
 * `customer`, die Maske kennt `ex-kunde`; der Server kennt `conditions` und
 * `iban`, und die stehen gar nicht in der Maske, sondern in den Einstellungen.
 * Genau solche Punkte dürfen nicht an ein beliebiges Feld geheftet werden –
 * sie bekommen keine Zuordnung und erscheinen als Sammelmeldung über dem
 * Formular. Ein roter Rahmen um ein Feld, das man gar nicht ändern kann, wäre
 * schlimmer als keiner.
 */

/** Serverfeld → Kennung der Eingabe im Formular. */
export type Feldkarte = Record<string, string>

/** Die Zuordnung für die Express-Erfassung (Rechnung und Offerte). */
export const EXPRESS_FELDER: Feldkarte = {
  customer: 'ex-kunde',
  customer_name: 'ex-kunde',
  /*
    Die unvollständige Kundenadresse gehört an die Kundenauswahl.

    Sie fehlte in dieser Tabelle, und damit landete der wichtigste Satz der
    ganzen Prüfung in der Sammelmeldung über dem Formular – «die Adresse von
    … ist unvollständig, es fehlt die PLZ», ohne roten Rahmen, ohne Sprung.
    Gemeldet aus dem Kundenbetrieb als «Pflichtfelder werden nicht markiert».

    Das Feld, an dem der Mensch handeln kann, ist die Kundenauswahl: Dort
    sieht er, WELCHER Kunde gemeint ist, und von dort führt der Weg zum
    Kundenstamm. Die Adresse selbst steht nicht in dieser Maske – ein roter
    Rahmen um ein Feld, das es hier nicht gibt, wäre falsch.
  */
  customer_address: 'ex-kunde',
  lines: 'ex-zeilen',
  total: 'ex-total',
  type: 'ex-zeilen'
}

export interface Feldbefund {
  /** Fehler je Eingabekennung – für Rahmen und Text am Feld. */
  amFeld: Record<string, string>
  /** Was sich keinem Feld zuordnen liess – als Sammelmeldung über dem Formular. */
  ohneFeld: string[]
  /** Die erste Eingabekennung mit Fehler – dorthin wird gesprungen. */
  erste: string | null
}

/**
 * Aus einem Serverfehler die Feldbefunde machen.
 *
 * Kennt der Fehler keine Feldliste – ein Netzfehler, ein 500er –, bleibt alles
 * leer und der Aufrufer zeigt wie bisher die Meldung an. Diese Funktion
 * erfindet keine Zuordnung.
 */
export function ausFehler(err: unknown, karte: Feldkarte): Feldbefund {
  const leer: Feldbefund = { amFeld: {}, ohneFeld: [], erste: null }
  if (!(err instanceof ApiError)) return leer
  const punkte: FieldProblem[] = err.options.fields ?? []
  if (punkte.length === 0) return leer

  const amFeld: Record<string, string> = {}
  const ohneFeld: string[] = []
  let erste: string | null = null

  for (const p of punkte) {
    const ziel = karte[p.feld]
    if (!ziel) {
      ohneFeld.push(p.problem)
      continue
    }
    /*
      Mehrere Punkte auf demselben Feld: Der erste gilt. Zwei Sätze unter
      einer Eingabe sind zwei Aufforderungen gleichzeitig, und man kann nur
      eine zuerst befolgen.
    */
    if (!amFeld[ziel]) amFeld[ziel] = p.problem
    if (!erste) erste = ziel
  }
  return { amFeld, ohneFeld, erste }
}

/**
 * Zum ersten fehlerhaften Feld springen und hineinsetzen.
 *
 * Erst scrollen, dann fokussieren: Ein `focus()` allein springt in manchen
 * Browsern hart und ohne Mitte, und auf dem Telefon landet das Feld dann
 * unter der eingeblendeten Tastatur. `preventScroll` trennt die beiden
 * Schritte, damit das weiche Scrollen gilt.
 */
export function springeZu(feldId: string | null): void {
  if (!feldId || typeof document === 'undefined') return
  const el = document.getElementById(feldId)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    el.focus({ preventScroll: true })
  } else {
    ;(el as HTMLElement).focus?.({ preventScroll: true })
  }
}
